import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useParams, useHistory } from "react-router";
import Header from "../header/header";
import Footer from "../footer/footer";
import NavBar from "../nav/nav-bar";
import Service from "../webservice/http";
import ClipLoader from "react-spinners/ClipLoader";
import { WaveSurfer, WaveForm, Region } from "wavesurfer-react";
import RegionsPlugin from "wavesurfer.js/dist/plugin/wavesurfer.regions.min";
import TimelinePlugin from "wavesurfer.js/dist/plugin/wavesurfer.timeline.min";
import {
  NotificationContainer,
  NotificationManager,
} from "react-notifications";
import { storage } from "../utils/storage";

const formatTime = (sec) => {
  if (typeof sec !== "number" || isNaN(sec)) return "-";
  return `${Math.floor(sec / 60)
    .toString()
    .padStart(2, "0")}:${Math.floor(sec % 60)
    .toString()
    .padStart(2, "0")}`;
};

const simpleDiff = (o, n) => {
  if (!o) return [{ value: n, added: true }];
  if (!n) return [{ value: o, removed: true }];
  const op = o.split(/(\b)/).filter((p) => p.length > 0);
  const np = n.split(/(\b)/).filter((p) => p.length > 0);
  let oi = 0,
    ni = 0;
  const res = [];
  while (oi < op.length || ni < np.length) {
    if (oi < op.length && ni < np.length && op[oi] === np[ni]) {
      res.push({ value: op[oi], equal: true });
      oi++;
      ni++;
    } else {
      let best = null;
      for (let i = 0; i < 20 && oi + i < op.length; i++)
        for (let j = 0; j < 20 && ni + j < np.length; j++) {
          if (i === 0 && j === 0) continue;
          if (op[oi + i] === np[ni + j]) {
            const sc =
              (/[a-zA-Z0-9]{2,}/.test(op[oi + i]) ? 10 : 1) * 10 - (i + j);
            if (!best || sc > best.score) best = { i, j, score: sc };
          }
        }
      if (best && best.score > 0) {
        const r = op.slice(oi, oi + best.i).join(""),
          a = np.slice(ni, ni + best.j).join("");
        if (r) res.push({ value: r, removed: true });
        if (a) res.push({ value: a, added: true });
        oi += best.i;
        ni += best.j;
      } else {
        if (oi < op.length) {
          res.push({ value: op[oi], removed: true });
          oi++;
        }
        if (ni < np.length) {
          res.push({ value: np[ni], added: true });
          ni++;
        }
      }
    }
  }
  const out = [];
  res.forEach((p) => {
    const l = out[out.length - 1];
    if (
      l &&
      ((l.removed && p.removed) || (l.added && p.added) || (l.equal && p.equal))
    )
      l.value += p.value;
    else out.push(p);
  });
  return out;
};

const VERSION_COLORS = {
  1: { bg: "hsla(180,95%,75%,0.9)", text: "hsla(180,70%,25%,1)" },
  2: { bg: "hsla(320,90%,80%,0.9)", text: "hsla(320,70%,25%,1)" },
  3: { bg: "hsla(30,100%,75%,0.9)", text: "hsla(30,70%,25%,1)" },
  4: { bg: "hsla(90,85%,70%,0.9)", text: "hsla(90,60%,25%,1)" },
  5: { bg: "hsla(350,95%,80%,0.9)", text: "hsla(350,70%,30%,1)" },
};
const getVersionColor = (v) => {
  if (v <= 5) return VERSION_COLORS[v] || VERSION_COLORS[1];
  const h = (v * 137.5) % 360;
  return { bg: `hsla(${h},85%,75%,0.9)`, text: `hsla(${h},70%,25%,1)` };
};

const CallTranscriptionAudit = () => {
  const { callID } = useParams();
  const history = useHistory();
  const services = useMemo(() => new Service(), []);

  const [callInsight, setCallInsight] = useState(null);
  const [isPending, setIsPending] = useState(true);
  const [reviewTranscription, setReviewTranscription] = useState(null);
  const [reviewPending, setReviewPending] = useState(false);

  const [showEnglish, setShowEnglish] = useState(true); // true=English, false=native; set by fetchData

  const [editableTranscriptions, setEditableTranscriptions] = useState([]);
  const [originalTranscriptions, setOriginalTranscriptions] = useState([]);
  const [isEditingAll, setIsEditingAll] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [edithistory, setEdithistory] = useState(null);
  const [lineVersionMap, setLineVersionMap] = useState({});
  const editableRefs = useRef([]);

  const [speakers, setSpeakers] = useState([]);
  const [emotions, setEmotions] = useState([]);
  const [regions, setRegions] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [timelineVis] = useState(true);
  const wavesurferRef = useRef();
  const regionsRef = useRef([]);

  const plugins = useMemo(
    () =>
      [
        { plugin: RegionsPlugin, options: { dragSelection: false } },
        timelineVis && {
          plugin: TimelinePlugin,
          options: {
            container: "#ta-timeline",
            color: "#000000",
            fontSize: "22px",
          },
        },
      ].filter(Boolean),
    [timelineVis],
  );

  const fetchReview = useCallback(() => {
    setReviewPending(true);
    services
      .get(`api/call/${callID}/edit_transcription/`)
      .then((res) => {
        setReviewPending(false);
        if (!res || res.code === "token_not_valid") return;
        setReviewTranscription(res);
      })
      .catch(() => setReviewPending(false));
  }, [callID, services]);

  const fetchData = useCallback(() => {
    setIsPending(true);
    services.get(`api/call/${callID}/call_insight/`).then((res) => {
      setIsPending(false);
      if (!res || res.code === "token_not_valid") {
        storage.clear();
        history.push("/login");
        return;
      }
      setCallInsight(res);
      if (res.language !== "English") setShowEnglish(false);
      try {
        wavesurferRef.current.load(res.audio_file);
      } catch {}
      // build speakers/emotions for filter
      const sp = [],
        em = [];
      (res.speech || []).forEach((s) => {
        if (!sp.includes(s.speaker)) sp.push(s.speaker);
        if (s.emotion && !em.includes(s.emotion)) em.push(s.emotion);
      });
      setSpeakers(sp);
      setEmotions(em);
    });
  }, [callID, services, history]);

  useEffect(() => {
    fetchData();
    fetchReview();
  }, []);

  const regionFilterFun = (type, filter) => {
    if (!callInsight) return;
    const colorMap = {
      agent: "rgba(255,196,226,0.4)",
      customer: "rgba(255,255,0,0.4)",
      Happy: "rgba(0,228,255,0.2)",
      Fearful: "rgba(0,64,255,0.2)",
      Angry: "rgba(58,255,0,0.2)",
      Sad: "rgba(255,0,255,0.2)",
      Calm: "rgba(255,0,0,0.2)",
    };
    let i = 1;
    const rd = [];
    callInsight.speech.forEach((s) => {
      const match =
        type === "speaker" ? s.speaker === filter : s.emotion === filter;
      if (match)
        rd.push({
          id: "r-" + i++,
          start: s.startTime,
          end: s.endTime,
          color: colorMap[filter] || "rgba(0,0,255,0.2)",
          data: { systemRegionId: i },
        });
    });
    setRegions(rd);
  };

  // build editable list from review data
  // falls back to callInsight.speech when no prior review edits exist
  useEffect(() => {
    if (!callInsight?.speech) return;
    // use reviewer speech if available, otherwise fall back to original Dataklout speech
    const srcSpeech =
      reviewTranscription?.speech?.length > 0
        ? reviewTranscription.speech
        : callInsight.speech || [];
    const mapped = srcSpeech.map((t) => ({
      ...t,
      text: !showEnglish ? t.early_dialogue : t.dialogue,
    }));
    const clone = JSON.parse(JSON.stringify(mapped));
    setEditableTranscriptions(mapped);
    setOriginalTranscriptions(clone);
    const eh = !showEnglish
      ? reviewTranscription?.edit_summary?.edited_early_sz_transcription
      : reviewTranscription?.edit_summary?.edited_serialized_transcription;
    setEdithistory(eh);
    const hd = !showEnglish
      ? reviewTranscription?.edit_history?.edited_early_sz_transcription
      : reviewTranscription?.edit_history?.edited_serialized_transcription;
    const lvm = {};
    if (Array.isArray(hd))
      hd.forEach((e) =>
        (e.changed_indices || []).forEach((idx) => {
          if (!lvm[idx] || e.version_number > lvm[idx])
            lvm[idx] = e.version_number;
        }),
      );
    setLineVersionMap(lvm);
    setIsEditingAll(false);
    setHasChanges(false);
    editableRefs.current = [];
  }, [callInsight, showEnglish, reviewTranscription]);

  useEffect(() => {
    if (!isEditingAll) return;
    requestAnimationFrame(() => {
      editableRefs.current.forEach((el, i) => {
        if (!el) return;
        const txt =
          editableTranscriptions?.[i]?.text ??
          editableTranscriptions?.[i]?.early_dialogue ??
          "";
        if (el.innerText !== txt) el.innerText = txt;
      });
    });
  }, [isEditingAll, editableTranscriptions]);

  const renderDiff = (index, item) => {
    const orig = callInsight?.speech?.[index];
    if (!orig) return item.text || item.early_dialogue || item.dialogue || "";
    // always compare english/primary dialogue
    const origText = orig.dialogue || orig.early_dialogue || "";
    const currText = item.text || item.early_dialogue || item.dialogue || "";
    if (origText === currText || isEditingAll) return currText;
    const diff = simpleDiff(origText, currText);
    const vNum = lineVersionMap[index] ?? (edithistory?.version_number || 1);
    const col = getVersionColor(vNum);
    return diff.map((p, i) => {
      if (p.added)
        return (
          <span
            key={i}
            style={{
              backgroundColor: col.bg,
              color: col.text,
              padding: "0 2px",
              borderRadius: "2px",
            }}
          >
            {p.value}
          </span>
        );
      if (p.removed)
        return (
          <span
            key={i}
            style={{
              textDecoration: "line-through",
              color: "#991b1b",
              backgroundColor: "#fee2e2",
              padding: "0 2px",
              borderRadius: "2px",
            }}
          >
            {p.value}
          </span>
        );
      return p.value;
    });
  };

  const placeCaretAtEnd = (el) => {
    if (!el) return;
    try {
      const r = document.createRange();
      r.selectNodeContents(el);
      r.collapse(false);
      const s = window.getSelection();
      s.removeAllRanges();
      s.addRange(r);
    } catch {}
  };
  const handleLineChange = (index, text) => {
    setEditableTranscriptions((prev) => {
      const c = [...prev];
      if (!c[index]) return prev;
      c[index] = { ...c[index], text };
      return c;
    });
    setHasChanges(true);
  };
  const handleCancelAll = () => {
    setEditableTranscriptions(
      JSON.parse(JSON.stringify(originalTranscriptions)),
    );
    setIsEditingAll(false);
    setHasChanges(false);
  };
  const handleSaveAll = () => {
    if (!hasChanges) return;
    const payload = (editableTranscriptions || []).map((t, index) => ({
      index,
      dialogue: t.text,
    }));
    // match original call-insight.js: only use serialized if showEnglish=true
    const body = showEnglish
      ? { updates_serialized: payload }
      : { updates_early_sz: payload };
    services
      .post(`api/call/${callID}/edit_transcription/`, body)
      .then((res) => {
        if (!res || res === "TypeError: Failed to fetch") return;
        setOriginalTranscriptions(
          JSON.parse(JSON.stringify(editableTranscriptions)),
        );
        setIsEditingAll(false);
        setHasChanges(false);
        NotificationManager.success("Transcription updated", "Success");
        fetchReview();
      });
  };

  const regionCreatedHandler = useCallback((region) => {
    if (region.data.systemRegionId) return;
    setRegions([
      ...regionsRef.current,
      { ...region, data: { ...region.data, systemRegionId: -1 } },
    ]);
  }, []);

  const handleWSMount = useCallback(
    (ws) => {
      wavesurferRef.current = ws;
      if (!ws) return;
      ws.params.waveColor = "#2196f3";
      ws.params.progressColor = "#000000";
      ws.params.responsive = true;
      ws.params.fillParent = true;
      ws.params.scrollParent = true;
      ws.setHeight(180);
      ws.on("region-created", regionCreatedHandler);
      ws.on("play", () => setIsPlaying(true));
      ws.on("pause", () => setIsPlaying(false));
      ws.on("finish", () => setIsPlaying(false));
      if (window) window.surferidze = ws;
    },
    [regionCreatedHandler],
  );

  const handleRegionUpdate = useCallback(() => {}, []);
  useEffect(
    () => () => {
      try {
        wavesurferRef.current.pause();
      } catch {}
    },
    [],
  );

  const isAgent = storage.getItem("role")?.toLowerCase().includes("agent");
  const linkStyle = (bg) => ({
    cursor: "cell",
    backgroundColor: bg,
    fontSize: "15px",
    color: "black",
    padding: "8px",
    borderRadius: "15px",
  });

  return (
    <div className="dashboard-body">
      <Header />
      <NavBar />
      <NotificationContainer />
      <section className="custom-wrapper-glife">
        <div className="container-fluid">
          {/* ── HEADER – matches call-insight.js style ── */}
          <div className="my-call">
            <div className="my-calls-column">
              <div className="calls-top-pannel">
                <div className="row">
                  <div className="col-lg-12">
                    <div className="pannel-heading clearfix div-padding">
                      <div className="pannel-heading-icon">
                        <i className="flaticon-incoming-call" />
                      </div>
                      <div className="pannel-heading-info">
                        <p>Transcription Audit</p>
                        <h3 className="detail-heading">
                          Customer Name &amp; ID :{" "}
                          <span className="detail-heading-span">
                            {callInsight?.customer || "—"}
                          </span>
                          <span>
                            &nbsp;(Ref# :{" "}
                            {callInsight?.call_reference || callID})
                          </span>
                        </h3>
                        <h3 className="detail-heading">
                          Agent Name :{" "}
                          <span className="detail-heading-span">
                            {callInsight?.agent || "—"}
                          </span>
                        </h3>
                      </div>
                      <div
                        className="pull-right flex items-center mt-2"
                        style={{ gap: "8px" }}
                      >
                        {edithistory?.version_number > 0 && (
                          <button
                            className="btn text-white hover:text-white"
                            style={{ backgroundColor: "#5BC0DE" }}
                            onClick={() =>
                              history.push(
                                `/call/${callID}/transcription-audit`,
                                { displayEnglish: showEnglish },
                              )
                            }
                          >
                            Version History
                          </button>
                        )}
                        <button
                          className="btn text-white hover:text-white"
                          style={{ backgroundColor: "#5BC0DE" }}
                          onClick={() =>
                            history.push(`/call/${callID}/call-insight`)
                          }
                        >
                          ← Back to Call Insight
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {isPending ? (
            <div className="empty-call" style={{ height: "400px" }}>
              <ClipLoader color="#2056FF" size="50px" />
            </div>
          ) : (
            <>
              {/* ── AUDIO PLAYER – matches call-insight.js layout ── */}
              <div
                className="white-box-global call-records"
                style={{ marginBottom: "20px", paddingBottom: "14px" }}
              >
                {/* top bar: duration left, play right */}
                <div className="flex flex-wrap gap-2">
                  <div className="flex-1">
                    <div className="white-box-header">
                      <h3>Call Duration : {callInsight?.duration}</h3>
                    </div>
                  </div>
                  <div
                    className="flex-1"
                    style={{ padding: "10px", paddingRight: "20px" }}
                  >
                    <input
                      style={{
                        backgroundColor: "#5bc0de",
                        padding: "5px 10px",
                        borderRadius: "5px",
                        color: "white",
                      }}
                      className="pull-right"
                      type="submit"
                      onClick={() => {
                        try {
                          wavesurferRef.current.playPause();
                        } catch {}
                      }}
                      value={isPlaying ? "Pause" : "Play"}
                    />
                  </div>
                </div>

                {/* waveform */}
                <div
                  style={{
                    height: "220px",
                    paddingLeft: "20px",
                    paddingRight: "20px",
                  }}
                >
                  <WaveSurfer
                    plugins={plugins}
                    onMount={handleWSMount}
                    scrollParent="true"
                  >
                    <div id="ta-timeline" />
                    <WaveForm id="ta-waveform" style={{ width: "100%" }}>
                      {regions.map((r) => (
                        <Region
                          onUpdateEnd={handleRegionUpdate}
                          key={r.id}
                          {...r}
                        />
                      ))}
                    </WaveForm>
                  </WaveSurfer>
                </div>

                {/* speaker/emotion filter links below player */}
                <div
                  className="flex flex-wrap gap-2 justify-center"
                  style={{ marginTop: "8px" }}
                >
                  <div style={{ textAlign: "center" }}>
                    {speakers.map((sp, i) => (
                      <React.Fragment key={i}>
                        {sp === "agent" && (
                          <>
                            <a
                              onClick={() =>
                                regionFilterFun("speaker", "agent")
                              }
                              style={linkStyle("rgba(255,196,226,0.4)")}
                            >
                              Agent
                            </a>
                            &nbsp;
                          </>
                        )}
                        {sp === "customer" && (
                          <>
                            <a
                              onClick={() =>
                                regionFilterFun("speaker", "customer")
                              }
                              style={linkStyle("rgba(255,255,0,0.4)")}
                            >
                              Customer
                            </a>
                            &nbsp;
                          </>
                        )}
                      </React.Fragment>
                    ))}
                    {emotions.length > 0 && (
                      <a style={{ fontSize: "20px" }}>
                        &nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;&nbsp;
                      </a>
                    )}
                    {emotions.map((em, i) => (
                      <React.Fragment key={i}>
                        {em === "Happy" && (
                          <>
                            <a
                              onClick={() =>
                                regionFilterFun("emotion", "Happy")
                              }
                              style={linkStyle("rgba(0,228,255,0.2)")}
                            >
                              Happy
                            </a>
                            &nbsp;
                          </>
                        )}
                        {em === "Fearful" && (
                          <>
                            <a
                              onClick={() =>
                                regionFilterFun("emotion", "Fearful")
                              }
                              style={linkStyle("rgba(0,64,255,0.2)")}
                            >
                              Fearful
                            </a>
                            &nbsp;
                          </>
                        )}
                        {em === "Angry" && (
                          <>
                            <a
                              onClick={() =>
                                regionFilterFun("emotion", "Angry")
                              }
                              style={linkStyle("rgba(58,255,0,0.2)")}
                            >
                              Angry
                            </a>
                            &nbsp;
                          </>
                        )}
                        {em === "Sad" && (
                          <>
                            <a
                              onClick={() => regionFilterFun("emotion", "Sad")}
                              style={linkStyle("rgba(255,0,255,0.2)")}
                            >
                              Sad
                            </a>
                            &nbsp;
                          </>
                        )}
                        {em === "Calm" && (
                          <>
                            <a
                              onClick={() => regionFilterFun("emotion", "Calm")}
                              style={linkStyle("rgba(255,0,0,0.2)")}
                            >
                              Calm
                            </a>
                            &nbsp;
                          </>
                        )}
                      </React.Fragment>
                    ))}
                    {(speakers.length > 0 || emotions.length > 0) && (
                      <>
                        <a
                          onClick={() => setRegions([])}
                          style={{
                            ...linkStyle("#f3f4f6"),
                            border: "1px solid #ccc",
                          }}
                        >
                          Clear
                        </a>
                        &nbsp;
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* ── single language toggle above both boxes (left-aligned) ── */}
              {callInsight?.language && callInsight?.language !== "English" && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                    justifyContent: "flex-end",
                  }}
                >
                  <span
                    style={{
                      fontSize: "13px",
                      color: "#4a5372",
                      fontWeight: 500,
                    }}
                  >
                    Language :
                  </span>
                  <div className="toggle-switch" style={{ margin: 0 }}>
                    <label>
                      {showEnglish ? "English" : callInsight?.language}
                    </label>
                    <div className="toggle-switch-intregrate">
                      <input
                        type="checkbox"
                        id="ta-lang"
                        checked={showEnglish}
                        onChange={() => setShowEnglish((p) => !p)}
                        disabled={isEditingAll}
                      />
                      <label
                        htmlFor="ta-lang"
                        style={
                          isEditingAll
                            ? { pointerEvents: "none", opacity: 0.5 }
                            : {}
                        }
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* ── SIDE BY SIDE TRANSCRIPTIONS ── */}
              <div
                style={{ display: "flex", gap: "16px", alignItems: "stretch" }}
              >
                {/* LEFT: Dataklout Transcription */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    className="transcription-white-global"
                    style={{ paddingBottom: "8px" }}
                  >
                    <div className="transcription-box">
                      <div className="transcription-header clearfix">
                        <h4>Dataklout Transcription</h4>
                      </div>
                      <div
                        className="transcription-scrollable"
                        style={{ height: "360px" }}
                      >
                        <div className="transcription-info-wrapper clearfix">
                          {(callInsight?.speech || []).map((t, i) => (
                            <div
                              key={i}
                              className="transcription-customer-section clearfix flex items-center gap-8"
                            >
                              <h4>
                                {t.speaker_name?.split(" ")[0] ||
                                  (t.speaker === "agent"
                                    ? "Agent"
                                    : "Customer")}
                              </h4>
                              <div className="customer-info-right">
                                <span>{formatTime(t.startTime)}</span>
                                <p>
                                  {showEnglish ? t.dialogue : t.early_dialogue}
                                </p>
                                <span>{formatTime(t.endTime)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* RIGHT: Reviewer Transcription */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    className="transcription-white-global"
                    style={{ paddingBottom: "8px" }}
                  >
                    <div className="transcription-box">
                      <div
                        className="transcription-header clearfix"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          // justifyContent: "space-between",
                        }}
                      >
                        <h4 style={{ margin: 0 }}>Reviewer Transcription</h4>
                      </div>

                      <div
                        className="transcription-scrollable"
                        style={{
                          height: "360px",
                          position: "relative",
                          overflow: "auto",
                        }}
                      >
                        <div className="transcription-info-wrapper clearfix">
                          {reviewPending ? (
                            <div
                              className="empty-call"
                              style={{ height: "200px" }}
                            >
                              <ClipLoader color="#2056FF" size="50px" />
                            </div>
                          ) : (
                            (editableTranscriptions || []).map(
                              (item, index) => (
                                <div
                                  key={index}
                                  className="transcription-customer-section clearfix flex items-center gap-8"
                                >
                                  <h4>
                                    {item.speaker_name?.split(" ")[0] ||
                                      (item.speaker === "agent"
                                        ? "Agent"
                                        : "Customer")}
                                  </h4>
                                  <div className="customer-info-right">
                                    <span>
                                      {item?.startTime != null
                                        ? formatTime(item.startTime)
                                        : "N/A"}
                                    </span>
                                    <p
                                      className="transcription-text"
                                      contentEditable={isEditingAll}
                                      suppressContentEditableWarning
                                      ref={(el) => {
                                        // always store ref so focus works on first click
                                        editableRefs.current[index] = el;
                                      }}
                                      onFocus={(e) => {
                                        if (isEditingAll)
                                          placeCaretAtEnd(e.currentTarget);
                                      }}
                                      onBlur={(e) =>
                                        handleLineChange(
                                          index,
                                          e.currentTarget.innerText || "",
                                        )
                                      }
                                      onClick={(e) => {
                                        if (isAgent) return;
                                        e.stopPropagation();
                                        setHasChanges(true);
                                        if (!isEditingAll) {
                                          setIsEditingAll(true);
                                          const ti = index;
                                          setTimeout(() => {
                                            const el = editableRefs.current[ti];
                                            if (el) {
                                              placeCaretAtEnd(el);
                                              el.focus();
                                            }
                                          }, 0);
                                        }
                                      }}
                                      style={{
                                        cursor: isAgent
                                          ? "not-allowed"
                                          : "text",
                                        outline: isEditingAll ? "none" : "",
                                        boxShadow: "none",
                                        userSelect: isEditingAll
                                          ? "auto"
                                          : "text",
                                      }}
                                    >
                                      {isEditingAll
                                        ? item?.text ||
                                          item?.early_dialogue ||
                                          item?.dialogue ||
                                          ""
                                        : renderDiff(index, item)}
                                    </p>
                                    <span>
                                      {item?.endTime != null
                                        ? formatTime(item.endTime)
                                        : "N/A"}
                                    </span>
                                  </div>
                                </div>
                              ),
                            )
                          )}
                        </div>

                        {isEditingAll && (
                          <div
                            style={{
                              position: "sticky",
                              bottom: 0,
                              display: "flex",
                              justifyContent: "flex-end",
                              padding: "10px 12px",
                              zIndex: 20,
                              gap: 10,
                              background: "rgba(255,255,255,0.95)",
                              borderTop: "1px solid #e5e7eb",
                            }}
                          >
                            <button
                              onClick={handleCancelAll}
                              className="px-6 py-2 bg-gray-400 text-white rounded"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleSaveAll}
                              className="px-6 py-2 bg-blue-800 text-white rounded"
                              style={{
                                opacity: hasChanges ? 1 : 0.5,
                                cursor: hasChanges ? "pointer" : "not-allowed",
                              }}
                            >
                              Save
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* stats legend – pill chips matching app filter style */}
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      marginTop: "10px",
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <a
                      style={{
                        cursor: "default",
                        backgroundColor: "rgba(255,196,226,0.5)",
                        fontSize: "14px",
                        color: "black",
                        padding: "6px 14px",
                        borderRadius: "15px",
                      }}
                    >
                      Sentences Changed :&nbsp;
                      <strong>
                        {edithistory?.total_sentences_changes ?? 0}
                      </strong>
                    </a>
                    <a
                      style={{
                        cursor: "default",
                        backgroundColor: "rgba(255,220,0,0.4)",
                        fontSize: "14px",
                        color: "black",
                        padding: "6px 14px",
                        borderRadius: "15px",
                      }}
                    >
                      Words Changed :&nbsp;
                      <strong>{edithistory?.total_words_added ?? 0}</strong>
                    </a>
                    <a
                      onClick={() => {
                        if (edithistory?.version_number > 0)
                          history.push(`/call/${callID}/transcription-audit`, {
                            displayEnglish: showEnglish,
                          });
                      }}
                      style={{
                        cursor:
                          edithistory?.version_number > 0
                            ? "pointer"
                            : "default",
                        backgroundColor: "rgba(0,228,255,0.2)",
                        fontSize: "14px",
                        color: "black",
                        padding: "6px 14px",
                        borderRadius: "15px",
                        opacity: edithistory?.version_number > 0 ? 1 : 0.6,
                      }}
                    >
                      v<strong>{edithistory?.version_number || 0}</strong>
                      &nbsp;·&nbsp;
                      {edithistory?.edited_at
                        ? edithistory.edited_at.split(" ")[0]
                        : "Not Edited"}
                    </a>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default CallTranscriptionAudit;
